import { useLocation, useNavigate } from 'react-router-dom';
import { resumeEmailLinkDestination } from './utils/emailLinkNavigation';
import { EmailLinkSecurity } from './components/EmailLinkSecurity';
/**
 * /security — the one place a user manages how they sign in.
 *
 * Standardised across tenants. Shows the STATUS of every sign-in method the
 * tenant enables (email · phone · username/password · passkeys), lets the user
 * add / change / remove each one, and shows their own login history. The point:
 * give every user MORE THAN ONE way in, so if one method fails they have another.
 *
 * Which methods appear is driven entirely by the tenant's auth config
 * (tenantAuthConfig) — the same record edited on the master admin tenant page.
 *
 * Layout: self-contained (own Navbar + Footer), mirroring MyDetails.tsx.
 */
import { Query, WebAuthnCredential, Mutation, MutationDeletePasskeyArgs, MutationRenamePasskeyArgs } from '../../generated/graphql';
import React, { useMemo, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Wrap,
    WrapItem,
    Card,
    CardHeader,
    CardBody,
    Badge,
    Button,
    IconButton,
    Input,
    FormControl,
    FormLabel,
    Spinner,
    Alert,
    AlertIcon,
    AlertDescription,
    Divider,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    useColorMode,
    useDisclosure,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
} from "@chakra-ui/react";
import { FiMail, FiPhone, FiKey, FiUser, FiTrash2, FiEdit2, FiPlus, FiShield } from "react-icons/fi";
import { useQuery, useMutation } from "@apollo/client";
import { getColor, getComponent, brandConfig } from "../../brandConfig";
import { useAuth } from "../../contexts/AuthContext";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
// Import from the specific submodules, NOT the "./index" barrel — the barrel
// re-exports this page, so importing it back would be a circular dependency.
import {
    GET_MY_CLIENT,
    GET_TENANT_AUTH_CONFIG,
    MY_PASSKEYS,
    GENERATE_PASSKEY_REGISTRATION_OPTIONS,
    VERIFY_PASSKEY_REGISTRATION,
    RENAME_PASSKEY,
    DELETE_PASSKEY,
    GET_LOGIN_ACTIVITIES,
} from "./queries";
// Every tooltip goes through the kit's SafeTooltip — it can never stay on screen (Polish rule 11).
import { SafeTooltip as Tooltip } from "./components/ui/SafeTooltip";
import { TwoStepVerificationCard } from "./components/TwoStepVerificationCard";
import { SignOutEverywhereCard } from "./components/SignOutEverywhereCard";
import { WhereSignedInCard } from "./components/WhereSignedInCard";
import { EmailChangeModal } from "./components/EmailChangeModal";
import { PhoneChangeModal } from "./components/PhoneChangeModal";
import { ChangePasswordCard } from "./components/ChangePasswordCard";
import { SetPasswordForm } from "./components/SetPasswordForm";
import { ForgotPasswordForm } from "./components/ForgotPasswordForm";
import {
    enrollPasskey,
    browserSupportsWebAuthn,
    friendlyPasskeyError,
} from "./utils/passkey";
import {
    classifyPasskeyForCurrentSite,
    isLocalhost,
    currentHost,
} from "./utils/passkeyEnv";

const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
    <Badge colorScheme={active ? "green" : "gray"} variant={active ? "solid" : "subtle"}>
        {active ? "Active" : "Not set up"}
    </Badge>
);

const formatDate = (value?: string | null): string => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
};

export default function SecurityPage() {
    const location = useLocation();
    const returnTo = new URLSearchParams(location.search).get("returnTo");
    const { colorMode } = useColorMode();
    const toast = useToast();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const bgMain = getColor("background.main", colorMode);
    const cardGradientBg = getColor("background.cardGradient", colorMode);
    const cardBorder = getColor("border.darkCard", colorMode);
    const textPrimary = getColor(colorMode === "light" ? "text.primary" : "text.primaryDark", colorMode);
    const textSecondary = getColor(colorMode === "light" ? "text.secondary" : "text.secondaryDark", colorMode);
    const textMuted = getColor(colorMode === "light" ? "text.muted" : "text.mutedDark", colorMode);

    // network-only: a stale Apollo cache entry (e.g. populated by AuthContext before
    // hasUsernamePassword was added to the query) must never mask the real status.
    const { data: clientData, loading: clientLoading, error: clientError, refetch: refetchClient } = useQuery<Pick<Query, 'myClient'>>(GET_MY_CLIENT, {
        fetchPolicy: "network-only",
    });
    const { data: configData, loading: configLoading, error: configError, refetch: refetchConfig } = useQuery<Pick<Query, 'tenantAuthConfig'>>(GET_TENANT_AUTH_CONFIG, { fetchPolicy: "cache-first" });

    const client = clientData?.myClient;
    const clientId: string | undefined = client?.id || user?.id;

    const authConfig = useMemo(
        () => ({
            enablePhoneAuth: configData?.tenantAuthConfig?.enablePhoneAuth ?? true,
            enableEmailAuth: configData?.tenantAuthConfig?.enableEmailAuth ?? true,
            enableUsernamePassword: configData?.tenantAuthConfig?.enableUsernamePassword ?? false,
            enablePasskeyAuth: configData?.tenantAuthConfig?.enablePasskeyAuth ?? false,
        }),
        [configData]
    );

    // ─── Email change ───────────────────────────────────────────────────────
    const [newEmail, setNewEmail] = useState("");
    const emailModal = useDisclosure();

    // ─── Phone change ───────────────────────────────────────────────────────
    const [newPhone, setNewPhone] = useState("");
    const phoneModal = useDisclosure();

    // ─── Passkeys ────────────────────────────────────────────────────────────
    const passkeysEnabled = authConfig.enablePasskeyAuth;
    const { data: passkeyData, loading: passkeysLoading, error: passkeysError, refetch: refetchPasskeys } = useQuery<Pick<Query, 'myPasskeys'>>(MY_PASSKEYS, {
        skip: !passkeysEnabled,
        fetchPolicy: "cache-and-network",
    });
    const passkeys = passkeyData?.myPasskeys || [];

    const [generateRegOptions] = useMutation(GENERATE_PASSKEY_REGISTRATION_OPTIONS);
    const [verifyRegistration] = useMutation(VERIFY_PASSKEY_REGISTRATION);
    const [renamePasskeyMut] = useMutation<Pick<Mutation, 'renamePasskey'>, MutationRenamePasskeyArgs>(RENAME_PASSKEY);
    const [deletePasskeyMut] = useMutation<Pick<Mutation, 'deletePasskey'>, MutationDeletePasskeyArgs>(DELETE_PASSKEY);

    const nameModal = useDisclosure();
    const [passkeyName, setPasskeyName] = useState("");
    const [renameTargetId, setRenameTargetId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    // Password reset (for users who've forgotten their current password) — verify via
    // an email/phone code instead of entering the current password.
    const [showReset, setShowReset] = useState(false);
    const fpColors = {
        formLabelColor: getComponent("form", "labelColor", colorMode),
        formFieldBg: getComponent("form", "fieldBg", colorMode),
        formFieldBorder: getComponent("form", "fieldBorder", colorMode),
        formFieldBorderFocus: getComponent("form", "fieldBorderFocus", colorMode),
        textPrimary,
        textSecondary,
        textMuted,
        accentBlue: brandConfig.colors.primary,
        accentHover: brandConfig.colors.primaryHover,
    };

    const deleteDialog = useDisclosure();
    const [deleteTarget, setDeleteTarget] = useState<WebAuthnCredential | null>(null);
    const cancelRef = React.useRef<HTMLButtonElement>(null);

    const openAddPasskey = () => {
        setRenameTargetId(null);
        setPasskeyName(defaultPasskeyName());
        nameModal.onOpen();
    };
    const openRenamePasskey = (pk: WebAuthnCredential) => {
        setRenameTargetId(pk.id);
        setPasskeyName(pk.name || "");
        nameModal.onOpen();
    };

    const submitNameModal = async () => {
        const name = passkeyName.trim() || "Passkey";
        setBusy(true);
        try {
            if (renameTargetId) {
                const result = await renamePasskeyMut({ variables: { input: { id: renameTargetId, name } } });
                if (!result.data?.renamePasskey) throw new Error('The passkey was not renamed. Please retry.');
                toast({ title: "Passkey renamed", status: "success", duration: 2500 });
            } else {
                await enrollPasskey(generateRegOptions, verifyRegistration, name);
                toast({ title: "Passkey added", description: "You can now sign in with this passkey.", status: "success", duration: 3500 });
            }
            nameModal.onClose();
            await refetchPasskeys();
        } catch (err: any) {
            toast({ title: "Passkey error", description: friendlyPasskeyError(err), status: "error", duration: 5000 });
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            const result = await deletePasskeyMut({ variables: { id: deleteTarget.id } });
            if (!result.data?.deletePasskey) throw new Error('The passkey was not removed. Please retry.');
            toast({ title: "Passkey removed", status: "success", duration: 2500 });
            deleteDialog.onClose();
            setDeleteTarget(null);
            await refetchPasskeys();
        } catch (err: any) {
            toast({ title: "Could not remove passkey", description: err.message, status: "error", duration: 5000 });
        } finally {
            setBusy(false);
        }
    };

    // ─── Login history ────────────────────────────────────────────────────────
    const { data: activityData, loading: activityLoading, error: activityError } = useQuery<Pick<Query, 'loginActivities'>>(GET_LOGIN_ACTIVITIES, {
        skip: !clientId,
        variables: { clientId: clientId || "", limit: 25, offset: 0 },
        fetchPolicy: "cache-and-network",
    });
    const activities = activityData?.loginActivities || [];

    // ─── Derived status ─────────────────────────────────────────────────────
    const hasEmail = !!client?.email;
    const hasPhone = !!client?.phoneNumber;
    // Source of truth = backend's hasUsernamePassword (true when both a username AND a
    // passwordHash exist). passwordChangedAt is unreliable — it's null for accounts whose
    // password predates that field or was set via a path that didn't stamp it.
    const hasPassword = !!client?.hasUsernamePassword;
    const hasUsername = !!client?.username;
    const hasPasskeys = passkeys.some(key => classifyPasskeyForCurrentSite(key).worksHere);

    const activeMethodCount =
        (authConfig.enableEmailAuth && hasEmail ? 1 : 0) +
        (authConfig.enablePhoneAuth && hasPhone ? 1 : 0) +
        (authConfig.enableUsernamePassword && hasPassword ? 1 : 0) +
        (passkeysEnabled && hasPasskeys ? 1 : 0);

    if (configLoading || (clientLoading && !client)) {
        return (
            <Box minH="100vh" bg={bgMain}>
                <NavbarWithCallToAction />
                <Container maxW="container.md" py={16} textAlign="center">
                    <Spinner size="xl" />
                </Container>
                <FooterWithFourColumns />
            </Box>
        );
    }

    if (clientError || configError || !client || !configData?.tenantAuthConfig) return <Box p={8}><Alert status="error"><AlertIcon />Could not load your sign-in settings.</Alert><Tooltip label="Reload your profile and this site's authentication policy."><Button mt={4} onClick={() => { void refetchClient(); void refetchConfig(); }}>Retry</Button></Tooltip></Box>;
    const enabledMethodCount = Object.values(authConfig).filter(Boolean).length;
    return (
        <Box minH="100vh" bg={bgMain} display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            <Container maxW="container.lg" py={{ base: 6, md: 10 }} flex="1">
                <VStack align="stretch" spacing={6}>
                    {/* Header */}
                    <Box>
                        <HStack spacing={3} mb={1}>
                            <Box as={FiShield} color={textPrimary} fontSize="2xl" aria-hidden />
                            <Heading size="lg" color={textPrimary}>Security &amp; Sign-in</Heading>
                        </HStack>
                        <Text color={textSecondary}>
                            Manage the sign-in methods this site allows. Verify a new email or mobile before replacing it.
                        </Text>
                    </Box>

                    {returnTo && <Tooltip label="Return to the page you were using. You can finish optional sign-in setup later."><Button alignSelf="start" onClick={() => resumeEmailLinkDestination(returnTo)}>Continue to where I left off</Button></Tooltip>}
                    {authConfig.enableEmailAuth && ['link', 'link_and_code'].includes(configData.tenantAuthConfig.emailSignInMode) && <EmailLinkSecurity />}

                    {/* Resilience banner */}
                    <Alert
                        status={activeMethodCount >= Math.min(2, enabledMethodCount) ? "success" : "info"}
                        borderRadius="md"
                        data-testid="resilience-banner"
                    >
                        <AlertIcon />
                        <AlertDescription>
                            {passkeysLoading || passkeysError ? "Checking available sign-in methods. The passkey list below shows its current status." : `You have ${activeMethodCount} available sign-in ${activeMethodCount === 1 ? "method" : "methods"} for this site.${enabledMethodCount > 1 && activeMethodCount < 2 ? " You can add another enabled method as a backup." : ""}`}
                        </AlertDescription>
                    </Alert>

                    {/* Email */}
                    {authConfig.enableEmailAuth && (
                        <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                            <CardHeader pb={2}>
                                <HStack justify="space-between">
                                    <HStack spacing={3}>
                                        <Box as={FiMail} color={textPrimary} aria-hidden />
                                        <Heading size="sm" color={textPrimary}>Email</Heading>
                                    </HStack>
                                    <StatusBadge active={hasEmail} />
                                </HStack>
                            </CardHeader>
                            <CardBody pt={0}>
                                <Text color={textSecondary} mb={3}>
                                    {hasEmail ? client.email : "No email on file. Add one to sign in by email."}
                                </Text>
                                <Wrap spacing={3} align="center">
                                    <WrapItem flex="1" minW="200px">
                                        <FormControl>
                                            <FormLabel fontSize="sm" color={textMuted} mb={1}>
                                                {hasEmail ? "New email address" : "Email address"}
                                            </FormLabel>
                                            <Input
                                                type="email"
                                                placeholder="you@example.com"
                                                value={newEmail}
                                                onChange={(e) => setNewEmail(e.target.value)}
                                                aria-label="New email address"
                                            />
                                        </FormControl>
                                    </WrapItem>
                                    <WrapItem alignSelf="flex-end">
                                        <Tooltip
                                            hasArrow
                                            openDelay={300}
                                            placement="top"
                                            whiteSpace="pre-line"
                                            shouldWrapChildren
                                            label={`${hasEmail ? "Change your sign-in email." : "Add an email so you can sign in by email."}\n\nWhat happens when you click:\n  1. We send a verification code to the address you typed\n  2. You enter that code to prove it's yours\n  3. Your account email is updated\n  4. You can then sign in (and get codes) at the new address`}
                                        >
                                            <Button
                                                colorScheme="blue"
                                                isDisabled={!newEmail.trim()}
                                                onClick={emailModal.onOpen}
                                            >
                                                {hasEmail ? "Change email" : "Add email"}
                                            </Button>
                                        </Tooltip>
                                    </WrapItem>
                                </Wrap>
                            </CardBody>
                        </Card>
                    )}

                    {/* Phone */}
                    {authConfig.enablePhoneAuth && (
                        <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                            <CardHeader pb={2}>
                                <HStack justify="space-between">
                                    <HStack spacing={3}>
                                        <Box as={FiPhone} color={textPrimary} aria-hidden />
                                        <Heading size="sm" color={textPrimary}>Phone (SMS)</Heading>
                                    </HStack>
                                    <StatusBadge active={hasPhone} />
                                </HStack>
                            </CardHeader>
                            <CardBody pt={0}>
                                <Text color={textSecondary} mb={3}>
                                    {hasPhone ? client.phoneNumber : "No phone on file. Add one to sign in by SMS code."}
                                </Text>
                                <Wrap spacing={3} align="center">
                                    <WrapItem flex="1" minW="200px">
                                        <FormControl>
                                            <FormLabel fontSize="sm" color={textMuted} mb={1}>
                                                {hasPhone ? "New phone number" : "Phone number"}
                                            </FormLabel>
                                            <Input
                                                type="tel"
                                                placeholder="+61 400 000 000"
                                                value={newPhone}
                                                onChange={(e) => setNewPhone(e.target.value)}
                                                aria-label="New phone number"
                                            />
                                        </FormControl>
                                    </WrapItem>
                                    <WrapItem alignSelf="flex-end">
                                        <Tooltip
                                            hasArrow
                                            openDelay={300}
                                            placement="top"
                                            whiteSpace="pre-line"
                                            shouldWrapChildren
                                            label={`${hasPhone ? "Change your sign-in phone number." : "Add a phone number so you can sign in by SMS."}\n\nWhat happens when you click:\n  1. We text a verification code to the number you typed\n  2. You enter that code to prove it's yours\n  3. Your account phone number is updated\n  4. You can then sign in (and get codes) by SMS`}
                                        >
                                            <Button
                                                colorScheme="blue"
                                                isDisabled={!newPhone.trim()}
                                                onClick={phoneModal.onOpen}
                                            >
                                                {hasPhone ? "Change phone" : "Add phone"}
                                            </Button>
                                        </Tooltip>
                                    </WrapItem>
                                </Wrap>
                            </CardBody>
                        </Card>
                    )}

                    {/* Username / Password */}
                    {authConfig.enableUsernamePassword && (
                        <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                            <CardHeader pb={2}>
                                <HStack justify="space-between">
                                    <HStack spacing={3}>
                                        <Box as={FiUser} color={textPrimary} aria-hidden />
                                        <Heading size="sm" color={textPrimary}>Username &amp; Password</Heading>
                                    </HStack>
                                    <StatusBadge active={hasPassword} />
                                </HStack>
                            </CardHeader>
                            <CardBody pt={0}>
                                {hasUsername && (
                                    <Text color={textSecondary} mb={3}>
                                        Username: <strong>{client.username}</strong>
                                    </Text>
                                )}
                                {hasPassword ? (
                                    showReset ? (
                                        <>
                                            <Text color={textSecondary} mb={3}>
                                                Forgot your current password? Verify with a code sent to your email or
                                                phone, then set a new one — no current password needed.
                                            </Text>
                                            <ForgotPasswordForm
                                                {...fpColors}
                                                prefillEmail={client?.email || undefined}
                                                prefillPhone={client?.phoneNumber || undefined}
                                                onBack={() => setShowReset(false)}
                                                onSuccess={() => {
                                                    // A reset signs out EVERY device, this one included (2026-09-28).
                                                    toast({ title: "Password reset", description: "Every device was signed out, including this one. Sign in with your new password.", status: "success", duration: 8000, isClosable: true });
                                                    setShowReset(false);
                                                    logout();
                                                    navigate("/login");
                                                }}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <ChangePasswordCard />
                                            <Tooltip
                                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line"
                                                label={"Can't remember your current password?\n\nWhat happens when you click:\n  1. We send a verification code to your email or phone\n  2. You enter the code (no current password needed)\n  3. You set a brand-new password\n  4. Every device is signed out, including this one — you sign in again with the new password"}
                                            >
                                                <Button mt={3} size="sm" variant="link" colorScheme="blue" onClick={() => setShowReset(true)}>
                                                    Forgot your current password? Reset it
                                                </Button>
                                            </Tooltip>
                                        </>
                                    )
                                ) : (
                                    <>
                                        <Text color={textSecondary} mb={3}>
                                            Add a username &amp; password as another way to sign in.
                                        </Text>
                                        <SetPasswordForm onSuccess={() => refetchClient()} />
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    )}

                    {/* Passkeys */}
                    {passkeysEnabled && (
                        <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                            <CardHeader pb={2}>
                                <HStack justify="space-between">
                                    <HStack spacing={3}>
                                        <Box as={FiKey} color={textPrimary} aria-hidden />
                                        <Heading size="sm" color={textPrimary}>Passkeys</Heading>
                                    </HStack>
                                    <StatusBadge active={hasPasskeys} />
                                </HStack>
                            </CardHeader>
                            <CardBody pt={0}>
                                <Text color={textSecondary} mb={3}>
                                    Sign in with Touch ID, Windows Hello, your phone, or a security key — no password to remember.
                                </Text>

                                {isLocalhost() && (
                                    <Alert status="info" borderRadius="md" mb={3} fontSize="sm" data-testid="passkey-devmode-tip">
                                        <AlertIcon />
                                        <AlertDescription>
                                            You're on <strong>{currentHost()}</strong> (local development).
                                            Passkeys are tied to the exact web address you create them on, so a
                                            passkey added here works <strong>only</strong> in local dev — it won't
                                            work on your live domain, and your live-domain passkeys won't appear here.
                                            Each passkey below is tagged with the site it belongs to.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {!browserSupportsWebAuthn() ? (
                                    <Alert status="info" borderRadius="md">
                                        <AlertIcon />
                                        <AlertDescription>
                                            This browser doesn’t support passkeys. Try a recent version of Safari, Chrome, or Edge.
                                        </AlertDescription>
                                    </Alert>
                                ) : (
                                    <>
                                        {passkeysError ? <Alert status="error"><AlertIcon />Could not load your passkeys. <Button onClick={() => void refetchPasskeys()}>Retry</Button></Alert> : passkeysLoading && !passkeys.length ? (
                                            <Spinner />
                                        ) : passkeys.length === 0 ? (
                                            <Text color={textMuted} mb={4}>No passkeys yet.</Text>
                                        ) : (
                                            <VStack align="stretch" spacing={2} mb={4} divider={<Divider />}>
                                                {[...passkeys]
                                                    .sort((a, b) =>
                                                        Number(classifyPasskeyForCurrentSite(b).worksHere) -
                                                        Number(classifyPasskeyForCurrentSite(a).worksHere)
                                                    )
                                                    .map((pk) => {
                                                    const scope = classifyPasskeyForCurrentSite(pk);
                                                    return (
                                                    <HStack key={pk.id} justify="space-between" flexWrap="wrap" data-testid="passkey-row">
                                                        <Box>
                                                            <HStack spacing={2} mb={0.5}>
                                                                <Text color={textPrimary} fontWeight="medium" overflowWrap="anywhere">{pk.name}</Text>
                                                                <Tooltip label={scope.detail} hasArrow openDelay={300} placement="top" whiteSpace="pre-line">
                                                                    <Badge
                                                                        colorScheme={scope.worksHere ? "green" : scope.scope === "unknown" ? "gray" : "orange"}
                                                                        variant="subtle"
                                                                        cursor="help"
                                                                        data-testid="passkey-scope-badge"
                                                                    >
                                                                        {scope.label}
                                                                    </Badge>
                                                                </Tooltip>
                                                            </HStack>
                                                            <Text color={textMuted} fontSize="xs">
                                                                {pk.host ? `${pk.host} · ` : ""}Added {formatDate(pk.createdAt)}
                                                                {pk.lastUsedAt ? ` · Last used ${formatDate(pk.lastUsedAt)}` : ""}
                                                                {pk.backedUp ? " · Synced" : ""}
                                                            </Text>
                                                        </Box>
                                                        <HStack>
                                                            <Tooltip
                                                                label={"Rename this passkey.\n\nWhat happens when you click:\n  1. A small dialog opens with the current name\n  2. You type a new label (e.g. “Work laptop”)\n  3. Saving updates the label only — the passkey keeps working exactly as before"}
                                                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line"
                                                            >
                                                                <IconButton
                                                                    aria-label={`Rename ${pk.name}`}
                                                                    icon={<FiEdit2 />}
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => openRenamePasskey(pk)}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                label={"Remove this passkey.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. The passkey is deleted from your account\n  3. You can no longer sign in with it (the saved key on your device becomes useless)\n  4. Other sign-in methods are unaffected"}
                                                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line"
                                                            >
                                                                <IconButton
                                                                    aria-label={`Remove ${pk.name}`}
                                                                    icon={<FiTrash2 />}
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    colorScheme="red"
                                                                    onClick={() => { setDeleteTarget(pk); deleteDialog.onOpen(); }}
                                                                />
                                                            </Tooltip>
                                                        </HStack>
                                                    </HStack>
                                                    );
                                                })}
                                            </VStack>
                                        )}
                                        <Tooltip
                                            hasArrow
                                            openDelay={300}
                                            placement="top"
                                            whiteSpace="pre-line"
                                            shouldWrapChildren
                                            label={"Set up a passkey on this device.\n\nWhat happens when you click:\n  1. You give the passkey a name\n  2. Your device prompts you (Touch ID, Windows Hello, security key, or your phone)\n  3. A passkey is saved to your account\n  4. Next time you can sign in with just your fingerprint/face — no password needed"}
                                        >
                                            <Button
                                                leftIcon={<FiPlus />}
                                                colorScheme="blue"
                                                onClick={openAddPasskey}
                                                isLoading={busy && !renameTargetId}
                                            >
                                                Add a passkey
                                            </Button>
                                        </Tooltip>
                                        <Text color={textMuted} fontSize="xs" mt={3}>
                                            Seeing passkeys in your device's sign-in prompt that aren't listed here?
                                            Those are stored in your device's password manager (on a Mac:
                                            System Settings → Passwords). Removing a passkey here deletes it from
                                            this account, but old or other-site passkeys must be deleted in your
                                            password manager too.
                                        </Text>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    )}

                    {/* Two-step verification (2026-09-26) — needs two DIFFERENT sign-in methods set up */}
                    <TwoStepVerificationCard
                        enabledFactors={{
                            passkey: passkeysEnabled,
                            pwd: authConfig.enableUsernamePassword,
                            email_code: authConfig.enableEmailAuth,
                            sms_code: authConfig.enablePhoneAuth,
                        }}
                    />

                    {/* Where you're signed in (2026-09-28, 1.5.0) — each sign-in with its own Sign out */}
                    <WhereSignedInCard />

                    {/* Sign out everywhere (2026-09-28) — every other device, this one stays signed in */}
                    <SignOutEverywhereCard />

                    {/* Login history */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader pb={2}>
                            <Heading size="sm" color={textPrimary}>Recent sign-in activity</Heading><Text fontSize="sm" color={textMuted}>Latest 25 events, shown in your local time.</Text>
                        </CardHeader>
                        <CardBody pt={0}>
                            {activityError ? <Alert status="error"><AlertIcon />Could not load sign-in activity. Please reload to retry.</Alert> : activityLoading && !activities.length ? (
                                <Spinner />
                            ) : activities.length === 0 ? (
                                <Text color={textMuted}>No sign-in activity yet.</Text>
                            ) : (
                                <TableContainer overflowX="auto">
                                    <Table size="sm" variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th>When</Th>
                                                <Th>Event</Th>
                                                <Th>Method</Th>
                                                <Th>IP</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {activities.map((a) => (
                                                <Tr key={a.id} data-testid="activity-row">
                                                    <Td color={textSecondary}>{formatDate(a.timestamp)}</Td>
                                                    <Td color={textSecondary}>{(a.action || "").replace(/_/g, " ")}</Td>
                                                    <Td color={textSecondary}>{a.method || "—"}</Td>
                                                    <Td color={textMuted} fontSize="xs">{a.ipAddress || "—"}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardBody>
                    </Card>
                </VStack>
            </Container>
            <FooterWithFourColumns />

            {/* Email change modal (collects code, verifies) */}
            {clientId && (
                <EmailChangeModal
                    isOpen={emailModal.isOpen}
                    onClose={emailModal.onClose}
                    clientId={clientId}
                    newEmail={newEmail.trim()}
                    onSuccess={() => { setNewEmail(""); emailModal.onClose(); refetchClient(); }}
                />
            )}

            {/* Phone change modal */}
            {clientId && (
                <PhoneChangeModal
                    isOpen={phoneModal.isOpen}
                    onClose={phoneModal.onClose}
                    clientId={clientId}
                    newPhoneNumber={newPhone.trim()}
                    onSuccess={() => { setNewPhone(""); phoneModal.onClose(); refetchClient(); }}
                />
            )}

            {/* Passkey name / rename modal */}
            <Modal isOpen={nameModal.isOpen} onClose={nameModal.onClose} size={{ base: "full", md: "md" }} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{renameTargetId ? "Rename passkey" : "Add a passkey"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Name this passkey</FormLabel>
                            <Input
                                value={passkeyName}
                                onChange={(e) => setPasskeyName(e.target.value)}
                                placeholder="e.g. My MacBook"
                                aria-label="Passkey name"
                            />
                            {!renameTargetId && (
                                <Text fontSize="xs" color={textMuted} mt={2}>
                                    Your device will ask you to confirm with Touch ID, Windows Hello, or your security key.
                                </Text>
                            )}
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Tooltip label="Close without saving. Nothing changes." hasArrow openDelay={300} placement="top">
                            <Button variant="ghost" mr={3} onClick={nameModal.onClose}>Cancel</Button>
                        </Tooltip>
                        <Tooltip
                            hasArrow openDelay={300} placement="top" whiteSpace="pre-line"
                            label={renameTargetId
                                ? "Save the new name.\n\nUpdates this passkey's label only — it keeps working exactly as before."
                                : "Create the passkey.\n\nWhat happens when you click:\n  1. Your device prompts you (Touch ID / Windows Hello / security key / phone)\n  2. Once you confirm, the passkey is saved to your account\n  3. You can sign in with it straight away — no password needed"}
                        >
                            <Button colorScheme="blue" onClick={submitNameModal} isLoading={busy}>
                                {renameTargetId ? "Save" : "Create passkey"}
                            </Button>
                        </Tooltip>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete passkey confirm */}
            <AlertDialog isOpen={deleteDialog.isOpen} leastDestructiveRef={cancelRef} onClose={deleteDialog.onClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Remove passkey</AlertDialogHeader>
                        <AlertDialogBody>
                            Remove “{deleteTarget?.name}”? You won’t be able to sign in with this passkey anymore.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Tooltip label="Keep the passkey. Nothing changes." hasArrow openDelay={300} placement="top">
                                <Button ref={cancelRef} onClick={deleteDialog.onClose}>Cancel</Button>
                            </Tooltip>
                            <Tooltip
                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line"
                                label={"Permanently remove this passkey.\n\nWhat happens when you click:\n  1. The passkey is deleted from your account now\n  2. You can no longer sign in with it\n  3. Your other sign-in methods still work"}
                            >
                                <Button colorScheme="red" onClick={confirmDelete} ml={3} isLoading={busy}>
                                    Remove passkey
                                </Button>
                            </Tooltip>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
}

/** A sensible default label based on the user's platform. */
function defaultPasskeyName(): string {
    if (typeof navigator === "undefined") return "Passkey";
    const ua = navigator.userAgent || "";
    let device = "Passkey";
    if (/Mac/i.test(ua)) device = "My Mac";
    else if (/Windows/i.test(ua)) device = "Windows Hello";
    else if (/iPhone|iPad/i.test(ua)) device = "My iPhone";
    else if (/Android/i.test(ua)) device = "My Android";
    // Append the site host (e.g. "localhost:3200") so a passkey's name says which
    // site it's for — useful when several local tenant FEs share rpID "localhost".
    const host = typeof window !== "undefined" ? window.location.host : "";
    return host ? `${device} · ${host}` : device;
}
